/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import debounce from 'lodash.debounce';
import {waitForElement} from '../utils/BrowserUtils';
import useHasMounted from './useHasMounted';

export interface UseElementResizeResult {
  width: number;
  height: number;
  top?: number;
  left?: number;
};

const SETTLE_DELAY      = 150;
const MAX_STABLE_CHECKS = 4;

export interface UseElementResizeOptions {
  // reports a size only once it has stopped changing - two measurements in a
  // row agree - instead of every intermediate size while it animates. The
  // first size is still reported right away ...
  settled?: boolean;
}

const isSameRect = (first: UseElementResizeResult, second: UseElementResizeResult): boolean => (
  !!first && !!second &&
  first.width === second.width &&
  first.height === second.height &&
  first.top === second.top &&
  first.left === second.left
);

/**
 * Defining useElementResize hook.
 * 
 * The useElementResize is a custom hook which listens for resize
 * events on a specific DOM element and returns its dimensions
 * after a fixed delay once resizing has finished.
 * 
 * This implementation is suitable when multiple layout changes
 * happen together and size should be measured only after the
 * UI has reasonably settled.
 * 
 * ```typescript
 *  
 *  import { useElementResize } from '@ion/core/src/hooks/useElementResize';
 * 
 *  useElementResize(
 *    'dlsMapContainer',
 *    false,
 *    (_event, element) => {
 *      // code that will be executed after resize settles
 *    }
 *  );
 * 
 * ```
 */
// the options object follows the three existing positional parameters ...
/* eslint-disable max-params */
export const useElementResize = (
  elementId: string,
  fetchAgain: boolean = false,
  callbackFn?: Function,
  {settled = false}: UseElementResizeOptions = {}
) => {
  /* eslint-enable max-params */
  const [dimentions, setDimentions] = useState<UseElementResizeResult>({
    width  : 0,
    height : 0
  });

  const isMounted         = useHasMounted();
  const settleIntervalRef = useRef<number | null>(null);
  // the last measured dimensions - compared synchronously, since React 18
  // may run a state updater lazily, after this check has returned ...
  const dimensionsRef     = useRef<UseElementResizeResult>(dimentions);
  // settled mode: the previous measurement, which the next must match ...
  const lastMeasureRef    = useRef<UseElementResizeResult>(null);

  const setSizeIfChanged = (
    container: HTMLElement,
    e?: any
  ) => {
    let changed = false;

    // a detached element measures 0 x 0 - keep the last real size until
    // its replacement is observed ...
    if (container.isConnected) {
      const {
        width,
        height,
        top, left
      } = container.getBoundingClientRect();
      const prev        = dimensionsRef.current;
      const measured    = {width, height, top, left};
      const isFirstSize = !prev.width && !prev.height;
      // settled mode holds a size until a second measurement confirms it,
      // reporting "changed" meanwhile so the checks carry on ...
      const isUnsettled = settled && !isFirstSize && !isSameRect(measured, lastMeasureRef.current);
      lastMeasureRef.current = measured;

      changed = prev.width !== width ||
        prev.height !== height ||
        prev.top !== top ||
        prev.left !== left;

      if (changed && !isUnsettled) {
        dimensionsRef.current = {width, height, top, left};
        setDimentions(dimensionsRef.current);
        callbackFn?.(e, container);
      }
    }
    return changed;
  };

  const eventListener = useCallback(debounce(
    (container: HTMLElement, e: any) => {
      let stableCount = 0;

      if (settleIntervalRef.current) {
        clearInterval(settleIntervalRef.current);
      }

      // Initial check
      setSizeIfChanged(container, e);

      settleIntervalRef.current = window.setInterval(() => {
        const didChange = setSizeIfChanged(container, e);
        if (!didChange) {
          stableCount += 1;
        } else {
          stableCount = 0;
        }

        if (stableCount >= MAX_STABLE_CHECKS) {
          clearInterval(settleIntervalRef.current!);
          settleIntervalRef.current = null;
        }
      }, SETTLE_DELAY);
    }, 100), [callbackFn]);

  useEffect(() => {
    if (!isMounted || !elementId) return;

    let observer: ResizeObserver;
    let cancelWait: () => void;

    // waits - without giving up - for the element, which can render well
    // after this hook mounts (e.g. a page still loading its translations
    // on a slow reload), then observes it. A replaced element is found
    // again instead of being measured detached ...
    const observeElement = function() {
      cancelWait = waitForElement({
        id          : elementId,
        maxAttempts : Infinity,
        callback    : (container: HTMLElement) => {
          observer = new ResizeObserver(e => {
            if (container.isConnected) {
              eventListener(container, e);
            } else {
              observer.disconnect();
              observeElement();
            }
          });

          observer.observe(container);

          // Initial measurement
          eventListener(container, null);
        }
      });
    };

    observeElement();

    return () => {
      cancelWait?.();
      observer?.disconnect();
      eventListener.cancel();

      if (settleIntervalRef.current) {
        clearInterval(settleIntervalRef.current);
        settleIntervalRef.current = null;
      }
    };
  }, [isMounted, elementId, eventListener, fetchAgain]);

  return dimentions;
};

export default useElementResize;
